<template>
  <div>
    <div class="subtitle-2 font-weight-bold mb-3">Cutover window (Optus booking, Sydney time)</div>
    <v-row dense align="start">
      <v-col cols="12" md="3">
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="form.date"
              label="Date"
              prepend-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker v-model="form.date" :min="today" @input="dateMenu = false" />
        </v-menu>
      </v-col>
      <v-col cols="6" md="2">
        <v-select
          v-model="form.start"
          :items="starts"
          label="Start (Sydney time)"
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="6" md="2">
        <v-select
          v-model="form.duration"
          :items="durations"
          label="Duration (min)"
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="12" md="5">
        <step-button
          name="canSetWindow"
          label="Save window"
          :steps="preflight.steps"
          :override="saveState"
          :busy="busy"
          :loading="loadingKey === 'window'"
          @click="saveWindow"
        />
      </v-col>
    </v-row>
    <div v-if="summary" class="mt-1 grey--text text--darken-2">{{ summary }}</div>
    <v-alert v-for="text in warnings" :key="text" type="warning" dense outlined class="mt-2 mb-0">{{ text }}</v-alert>
    <div v-if="savedText" class="mt-2 green--text text--darken-2">{{ savedText }}</div>
    <v-alert v-if="savedText" type="warning" dense outlined class="mt-2 mb-0">
      Window changed: download the IP report again and send it to the wholesalers.
    </v-alert>

    <v-divider class="my-4" />

    <div class="subtitle-2 font-weight-bold mb-1">Static IPs</div>
    <div class="mb-3">{{ statics.text }}</div>
    <step-button
      name="canPreAssign"
      :label="`Pre-assign ${statics.without} statics`"
      :steps="preflight.steps"
      :busy="busy"
      :loading="loadingKey === 'preassign'"
      @click="askPreAssign"
    />
    <step-button
      name="canRelease"
      :label="`Release ${statics.reserved} reservations`"
      :steps="preflight.steps"
      color="secondary"
      :busy="busy"
      :loading="loadingKey === 'release'"
      @click="askRelease"
    />
    <step-button
      name="canDownloadReport"
      label="IP change report..."
      :steps="preflight.steps"
      color="secondary"
      :busy="busy"
      :loading="loadingKey === 'report'"
      @click="openReport"
    />

    <v-alert v-if="lastBatch" :type="lastBatch.type" dense outlined class="mt-2 mb-0">
      {{ lastBatch.text }}
      <v-expansion-panels v-if="lastBatch.rows.length" accordion flat class="mt-1">
        <v-expansion-panel>
          <v-expansion-panel-header class="px-0">Rows ({{ lastBatch.rows.length }})</v-expansion-panel-header>
          <v-expansion-panel-content class="px-0">
            <ul class="mb-0">
              <li v-for="(r, i) in lastBatch.rows" :key="`row-${i}`">
                <strong>{{ r.avcId }}</strong> {{ r.outcome }}: {{ r.message }}
              </li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-alert>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mt-2 mb-0">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />

    <v-dialog v-model="reportOpen" max-width="600" persistent>
      <v-card>
        <v-card-title class="primary">
          <span class="white--text">IP change report</span>
        </v-card-title>
        <v-card-text class="pt-4 black--text">
          <template v-if="report">
            <v-select
              v-model="wholesaler"
              :items="wholesalerItems"
              label="Wholesaler"
              outlined
              dense
              hide-details
            />
            <div class="mt-3">{{ serviceCount }} services</div>
            <div class="caption grey--text text--darken-1">
              Dynamics get a new {{ preflight.poiState }} pool IP when they reconnect.
            </div>
          </template>
          <div v-else>Loading the report...</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="reportOpen = false">Close</v-btn>
          <v-btn color="primary" :disabled="!report" @click="downloadReport">Download CSV</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { toIpReportCsv } from "@/utils/cvcMigration";
import { downloadText } from "@/utils/csv";
import { windowWarnings } from "@/utils/zonedTime";
import {
  WINDOW_STARTS,
  WINDOW_DURATIONS,
  DEFAULT_WINDOW_DURATION,
  stepButton,
  windowFromForm,
  windowToForm,
  windowSummary,
  windowSavedText,
  staticsSummary,
  batchSummary,
  sydneyDate,
} from "@/utils/cvcMigrationPage";
import StepButton from "./step-button";
import SectionActions from "./section-actions";

// Section A: the cutover window and the static IPs. The window is typed as a
// Sydney wall clock and converted once, here, to UTC for the server.
export default {
  name: "CvcMigrationPlanning",
  components: { StepButton },
  mixins: [SectionActions],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    now: { type: Date, required: true },
  },
  data () {
    return {
      dateMenu: false,
      form: this.initialForm(),
      dirty: false,
      savedText: "",
      lastBatch: null,
      reportOpen: false,
      report: null,
      wholesaler: "",
      starts: WINDOW_STARTS,
      durations: WINDOW_DURATIONS,
    };
  },
  computed: {
    record () {
      return this.preflight.record;
    },
    today () {
      return sydneyDate(this.now);
    },
    windowRange () {
      return windowFromForm(this.form);
    },
    summary () {
      const w = this.windowRange;
      return w ? windowSummary(w.startUtc, w.endUtc, this.preflight.poiState, this.browserZone()) : "";
    },
    warnings () {
      const w = this.windowRange;
      return w ? windowWarnings(w.startUtc, w.endUtc, this.now) : [];
    },
    saveState () {
      const base = stepButton(this.preflight.steps, "canSetWindow");
      if (!base.disabled && !this.windowRange) return { disabled: true, reason: "Choose a date and a start time" };
      return base;
    },
    statics () {
      return staticsSummary(this.preflight);
    },
    // The record's window; refills the form when another session changes it.
    savedWindowKey () {
      return this.record ? `${this.record.cutoverWindowStart}|${this.record.cutoverWindowEnd}` : "";
    },
    wholesalerItems () {
      const names = [];
      ((this.report && this.report.rows) || []).forEach(r => {
        if (r.wholesalerName && !names.includes(r.wholesalerName)) names.push(r.wholesalerName);
      });
      return [{ text: "All wholesalers", value: "" }].concat(names.map(n => ({ text: n, value: n })));
    },
    serviceCount () {
      const rows = (this.report && this.report.rows) || [];
      return rows.filter(r => !this.wholesaler || r.wholesalerName === this.wholesaler).length;
    },
  },
  watch: {
    form: { deep: true, handler () { this.dirty = true; } },
    savedWindowKey () {
      if (this.dirty) return;
      this.form = this.initialForm();
      this.$nextTick(() => { this.dirty = false; });
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationSetWindow",
      "cvcMigrationPreAssignStatics",
      "cvcMigrationReleasePreAssignments",
      "cvcMigrationIpChangeReport",
    ]),
    initialForm () {
      const record = this.preflight.record;
      const saved = record && windowToForm(record.cutoverWindowStart, record.cutoverWindowEnd);
      return saved || { date: "", start: "10:00", duration: DEFAULT_WINDOW_DURATION };
    },
    browserZone () {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        return "";
      }
    },
    saveWindow () {
      const w = this.windowRange;
      if (!w) return;
      this.run("window", async () => {
        await this.cvcMigrationSetWindow({
          cvcId: this.cvcId,
          targetHostId: this.targetHostId,
          startUtc: w.startUtc,
          endUtc: w.endUtc,
        });
        this.savedText = windowSavedText(w.startUtc, w.endUtc, this.preflight.poiState);
        this.dirty = false;
      });
    },
    askPreAssign () {
      const text = `Reserve ${this.statics.without} new ${this.preflight.poiState} static IPs for this CVC? Live RADIUS is unchanged until Commit.`;
      this.ask(text, "Reserve", () => this.run("preassign", async () => {
        const batch = await this.cvcMigrationPreAssignStatics({ cvcId: this.cvcId, targetHostId: this.targetHostId });
        this.lastBatch = batchSummary("Pre-assign", batch, new Date(), this.preflight.poiState);
      }));
    },
    askRelease () {
      const text = `Return ${this.statics.reserved} reserved IPs to the ${this.preflight.poiState} pool?`;
      this.ask(text, "Release", () => this.run("release", async () => {
        const batch = await this.cvcMigrationReleasePreAssignments({ cvcId: this.cvcId });
        this.lastBatch = batchSummary("Release", batch, new Date(), this.preflight.poiState);
      }));
    },
    openReport () {
      this.report = null;
      this.wholesaler = "";
      this.run("report", async () => {
        this.report = await this.cvcMigrationIpChangeReport({ cvcId: this.cvcId, targetHostId: this.targetHostId });
        this.reportOpen = true;
      });
    },
    downloadReport () {
      const csv = toIpReportCsv(this.report, { wholesaler: this.wholesaler });
      downloadText(csv.filename, csv.text);
    },
  },
};
</script>
